/**
 * MobileStrategyDetail Component
 *
 * Mobile page for viewing and editing a strategy, with playbook list.
 * Route: /settings/strategies/:strategyId
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, AlertTriangle, Plus, MoreVertical } from 'lucide-react';

import {
  Button,
  EmptyState,
  Alert,
  useDisclosure,
  Breadcrumbs,
  BreadcrumbItem,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
} from '@enfomentis/ui';
import { useStrategiesPlaybooks } from '../context';
import { StrategyDetailForm, PlaybookListItem, UnsavedChangesDialog, MoveWithUnsavedChangesDialog, LastPlaybookWarningDialog } from '../components';
import { StrategyRenameModal } from '../StrategyRenameModal';
import { PlaybookRenameModal } from '../PlaybookRenameModal';
import { StrategyPickerModal } from '../StrategyPickerModal';

export const MobileStrategyDetail: React.FC = () => {
  const { strategyId } = useParams<{ strategyId: string }>();
  const navigate = useNavigate();
  const unsavedChangesDialog = useDisclosure();
  const renameModalDisclosure = useDisclosure();
  const [isAddPopoverOpen, setIsAddPopoverOpen] = useState(false);

  // Pending navigation target when showing unsaved changes dialog
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  // Move playbook modal state
  const movePickerDisclosure = useDisclosure();
  const moveUnsavedDisclosure = useDisclosure();
  const lastPlaybookWarningDisclosure = useDisclosure();
  const [pendingMovePlaybookId, setPendingMovePlaybookId] = useState<string | null>(null);
  const [pendingMoveTargetId, setPendingMoveTargetId] = useState<string | null>(null);

  // Copy playbook modal state
  const copyPickerDisclosure = useDisclosure();
  const [pendingCopyPlaybookId, setPendingCopyPlaybookId] = useState<string | null>(null);

  // Share playbook modal state
  const sharePickerDisclosure = useDisclosure();
  const [pendingSharePlaybookId, setPendingSharePlaybookId] = useState<string | null>(null);

  // Track which playbook's actions popover is open (null = none)
  const [playbookActionsPopoverId, setPlaybookActionsPopoverId] = useState<string | null>(null);

  // Playbook rename modal state
  const playbookRenameDisclosure = useDisclosure();
  const [pendingRenamePlaybookId, setPendingRenamePlaybookId] = useState<string | null>(null);

  // Strategy actions popover state
  const [isStrategyActionsPopoverOpen, setIsStrategyActionsPopoverOpen] = useState(false);

  const {
    // Strategy data
    strategies,
    loading,
    selectedStrategy,
    selectedStrategyId,
    showArchived,
    setShowArchived,
    selectStrategy,

    // Strategy dirty state
    localDescription,
    localPhilosophy,
    isDirty,
    isSaving,
    showConflictBanner,

    // Strategy actions
    setLocalDescription,
    setLocalPhilosophy,
    saveStrategy,
    discardStrategyChanges,
    archiveStrategy,
    restoreStrategy,
    acceptExternalChanges,
    keepEditing,
    reloadStrategies,

    // Playbook data
    playbooks,
    filteredPlaybooks,
    playbooksLoading,
    playbooksLoadError,
    showArchivedPlaybooks,
    setShowArchivedPlaybooks,
    reloadPlaybooks,

    // Playbook dirty state (for move operation)
    isPlaybookDirty,
    isPlaybookSaving,
    savePlaybook,
    discardPlaybookChanges,

    // Playbook actions
    unlinkPlaybook,
    archivePlaybookFromList,
    restorePlaybookFromList,
    movePlaybook,
    copyPlaybook,
    sharePlaybook,
  } = useStrategiesPlaybooks();

  // Validate and sync URL param to context on mount/change
  useEffect(() => {
    // Wait for strategies to load before any validation
    if (loading) {
      return;
    }

    if (!strategyId) {
      navigate('/settings/strategies', { replace: true });
      return;
    }

    // Validate against full strategies array (not filtered)
    const strategy = strategies.find((s) => s.id === strategyId);

    if (!strategy) {
      // Strategy not found - redirect to list
      navigate('/settings/strategies', { replace: true });
      return;
    }

    // Auto-enable showArchived if strategy is archived and filter is off
    if (strategy.status === 'archived' && !showArchived) {
      setShowArchived(true);
    }

    // Sync to context if not already selected
    if (selectedStrategyId !== strategyId) {
      selectStrategy(strategyId);
    }
  }, [strategyId, strategies, loading, selectedStrategyId, showArchived, setShowArchived, selectStrategy, navigate]);

  // Handle back navigation with dirty check
  const handleBack = useCallback(() => {
    if (isDirty) {
      setPendingNavigation('/settings/strategies');
      unsavedChangesDialog.onOpen();
    } else {
      navigate('/settings/strategies');
    }
  }, [isDirty, navigate, unsavedChangesDialog]);

  // Handle navigation to Settings with dirty check
  const handleNavigateToSettings = useCallback(() => {
    if (isDirty) {
      setPendingNavigation('/settings');
      unsavedChangesDialog.onOpen();
    } else {
      navigate('/settings');
    }
  }, [isDirty, navigate, unsavedChangesDialog]);

  // Handle discard from dialog
  const handleDiscard = useCallback(() => {
    discardStrategyChanges();
    unsavedChangesDialog.onClose();
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  }, [discardStrategyChanges, unsavedChangesDialog, pendingNavigation, navigate]);

  // Handle playbook selection with dirty check
  const handlePlaybookSelect = useCallback(
    (playbookId: string) => {
      const targetPath = `/settings/strategies/${strategyId}/playbooks/${playbookId}`;
      if (isDirty) {
        setPendingNavigation(targetPath);
        unsavedChangesDialog.onOpen();
      } else {
        navigate(targetPath);
      }
    },
    [isDirty, strategyId, navigate, unsavedChangesDialog]
  );

  const handleShowArchivedPlaybooksToggle = useCallback(() => {
    setShowArchivedPlaybooks(!showArchivedPlaybooks);
  }, [showArchivedPlaybooks, setShowArchivedPlaybooks]);

  // Handle navigating to create new playbook
  const handleCreatePlaybook = useCallback(() => {
    setIsAddPopoverOpen(false);
    const targetPath = `/settings/strategies/${strategyId}/playbooks/new`;
    if (isDirty) {
      setPendingNavigation(targetPath);
      unsavedChangesDialog.onOpen();
    } else {
      navigate(targetPath);
    }
  }, [isDirty, strategyId, navigate, unsavedChangesDialog]);

  // Handle navigating to link existing playbook
  const handleLinkPlaybook = useCallback(() => {
    setIsAddPopoverOpen(false);
    const targetPath = `/settings/strategies/${strategyId}/playbooks/link`;
    if (isDirty) {
      setPendingNavigation(targetPath);
      unsavedChangesDialog.onOpen();
    } else {
      navigate(targetPath);
    }
  }, [isDirty, strategyId, navigate, unsavedChangesDialog]);

  // Handle rename success - reload strategies to get updated name
  const handleRenameSuccess = useCallback(() => {
    reloadStrategies();
  }, [reloadStrategies]);

  // ===========================================================================
  // Computed: Playbook Share Info
  // ===========================================================================

  /**
   * Map of playbookId -> array of strategy names that contain the playbook.
   * Only includes active, non-tombstoned strategies (same filter as desktop).
   */
  const playbookShareMap = useMemo(() => {
    const map = new Map<string, string[]>();

    // Filter to active, non-tombstoned strategies
    const activeStrategiesForSharing = strategies.filter(
      (s) => s.status === 'active' && !s.deletedAt
    );

    // For each playbook in each strategy, track which strategies contain it
    for (const strategy of activeStrategiesForSharing) {
      for (const playbookId of strategy.playbookIds || []) {
        const existing = map.get(playbookId) || [];
        existing.push(strategy.name);
        map.set(playbookId, existing);
      }
    }

    return map;
  }, [strategies]);

  // ===========================================================================
  // Playbook Action Handlers
  // ===========================================================================

  const handlePlaybookUnlink = useCallback(
    async (playbookId: string) => {
      await unlinkPlaybook(playbookId);
      setPlaybookActionsPopoverId(null);
    },
    [unlinkPlaybook]
  );

  const handlePlaybookArchiveFromList = useCallback(
    async (playbookId: string) => {
      await archivePlaybookFromList(playbookId);
      setPlaybookActionsPopoverId(null);
    },
    [archivePlaybookFromList]
  );

  const handlePlaybookRestoreFromList = useCallback(
    async (playbookId: string) => {
      await restorePlaybookFromList(playbookId);
      setPlaybookActionsPopoverId(null);
    },
    [restorePlaybookFromList]
  );

  // ===========================================================================
  // Move Playbook Handlers
  // ===========================================================================

  /**
   * Step 1: User clicks "Move to Strategy" in popover
   * - If dirty, show unsaved changes dialog
   * - If not dirty, open strategy picker
   */
  const handleMoveClick = useCallback(
    (playbookId: string) => {
      setPendingMovePlaybookId(playbookId);
      setPlaybookActionsPopoverId(null); // Close actions popover

      if (isPlaybookDirty) {
        moveUnsavedDisclosure.onOpen();
      } else {
        movePickerDisclosure.onOpen();
      }
    },
    [isPlaybookDirty, moveUnsavedDisclosure, movePickerDisclosure]
  );

  /**
   * Step 2a: User chose "Save & Move" from unsaved dialog
   */
  const handleSaveAndMove = useCallback(async () => {
    await savePlaybook();
    moveUnsavedDisclosure.onClose();
    movePickerDisclosure.onOpen();
  }, [savePlaybook, moveUnsavedDisclosure, movePickerDisclosure]);

  /**
   * Step 2b: User chose "Discard & Move" from unsaved dialog
   */
  const handleDiscardAndMove = useCallback(() => {
    discardPlaybookChanges();
    moveUnsavedDisclosure.onClose();
    movePickerDisclosure.onOpen();
  }, [discardPlaybookChanges, moveUnsavedDisclosure, movePickerDisclosure]);

  /**
   * Step 4: Execute the move operation
   * (Defined before handleMovePickerConfirm to satisfy dependency order)
   */
  const handleExecuteMove = useCallback(
    async (targetId: string) => {
      if (!pendingMovePlaybookId) return;

      await movePlaybook(pendingMovePlaybookId, targetId);

      // Reset state
      setPendingMovePlaybookId(null);
      setPendingMoveTargetId(null);
      lastPlaybookWarningDisclosure.onClose();
    },
    [pendingMovePlaybookId, movePlaybook, lastPlaybookWarningDisclosure]
  );

  /**
   * Step 3: User selected target strategy from picker
   * - If last playbook in strategy, show warning
   * - Otherwise, execute move
   */
  const handleMovePickerConfirm = useCallback(
    (strategyIds: string[]) => {
      const targetId = strategyIds[0];
      if (!targetId) return;

      setPendingMoveTargetId(targetId);
      movePickerDisclosure.onClose();

      // Check if this is the last playbook in the strategy
      const strategyPlaybookCount = selectedStrategy?.playbookIds?.length ?? 0;
      if (strategyPlaybookCount === 1) {
        lastPlaybookWarningDisclosure.onOpen();
      } else {
        // Execute move immediately
        handleExecuteMove(targetId);
      }
    },
    [selectedStrategy, movePickerDisclosure, lastPlaybookWarningDisclosure, handleExecuteMove]
  );

  /**
   * Step 4 (alternate): User confirmed last-playbook warning
   */
  const handleLastPlaybookWarningConfirm = useCallback(() => {
    if (pendingMoveTargetId) {
      handleExecuteMove(pendingMoveTargetId);
    }
  }, [pendingMoveTargetId, handleExecuteMove]);

  /**
   * Cancel move operation (from any dialog)
   */
  const handleMoveCancel = useCallback(() => {
    setPendingMovePlaybookId(null);
    setPendingMoveTargetId(null);
    moveUnsavedDisclosure.onClose();
    movePickerDisclosure.onClose();
    lastPlaybookWarningDisclosure.onClose();
  }, [moveUnsavedDisclosure, movePickerDisclosure, lastPlaybookWarningDisclosure]);

  // ===========================================================================
  // Copy Playbook Handlers
  // ===========================================================================

  /**
   * Handle "Duplicate" click - copy within current strategy
   */
  const handleDuplicateClick = useCallback(
    async (playbookId: string) => {
      if (!selectedStrategyId) return;
      setPlaybookActionsPopoverId(null); // Close actions popover
      await copyPlaybook(playbookId, selectedStrategyId);
    },
    [selectedStrategyId, copyPlaybook]
  );

  /**
   * Handle "Copy to Strategy..." click - open strategy picker
   */
  const handleCopyToStrategyClick = useCallback(
    (playbookId: string) => {
      setPendingCopyPlaybookId(playbookId);
      setPlaybookActionsPopoverId(null); // Close actions popover
      copyPickerDisclosure.onOpen();
    },
    [copyPickerDisclosure]
  );

  /**
   * Handle strategy picker confirmation for copy
   */
  const handleCopyPickerConfirm = useCallback(
    async (strategyIds: string[]) => {
      const targetId = strategyIds[0];
      if (!targetId || !pendingCopyPlaybookId) return;

      copyPickerDisclosure.onClose();
      await copyPlaybook(pendingCopyPlaybookId, targetId);
      setPendingCopyPlaybookId(null);
    },
    [pendingCopyPlaybookId, copyPlaybook, copyPickerDisclosure]
  );

  /**
   * Cancel copy operation
   */
  const handleCopyCancel = useCallback(() => {
    setPendingCopyPlaybookId(null);
    copyPickerDisclosure.onClose();
  }, [copyPickerDisclosure]);

  // ===========================================================================
  // Share Playbook Handlers
  // ===========================================================================

  /**
   * Handle "Share with Strategy..." click - open strategy picker
   */
  const handleShareClick = useCallback(
    (playbookId: string) => {
      setPendingSharePlaybookId(playbookId);
      setPlaybookActionsPopoverId(null); // Close actions popover
      sharePickerDisclosure.onOpen();
    },
    [sharePickerDisclosure]
  );

  /**
   * Handle strategy picker confirmation for share
   */
  const handleSharePickerConfirm = useCallback(
    async (strategyIds: string[]) => {
      if (!pendingSharePlaybookId || strategyIds.length === 0) return;

      sharePickerDisclosure.onClose();
      await sharePlaybook(pendingSharePlaybookId, strategyIds);
      setPendingSharePlaybookId(null);
    },
    [pendingSharePlaybookId, sharePlaybook, sharePickerDisclosure]
  );

  /**
   * Cancel share operation
   */
  const handleShareCancel = useCallback(() => {
    setPendingSharePlaybookId(null);
    sharePickerDisclosure.onClose();
  }, [sharePickerDisclosure]);

  // ===========================================================================
  // Rename Playbook Handlers (Mobile List Only)
  // ===========================================================================

  /**
   * Handle "Rename" click from playbook list popover
   */
  const handlePlaybookRenameClick = useCallback(
    (playbookId: string) => {
      setPendingRenamePlaybookId(playbookId);
      setPlaybookActionsPopoverId(null); // Close actions popover
      playbookRenameDisclosure.onOpen();
    },
    [playbookRenameDisclosure]
  );

  /**
   * Handle successful rename - reload playbooks to get updated name
   */
  const handlePlaybookRenameSuccess = useCallback(() => {
    reloadPlaybooks();
    setPendingRenamePlaybookId(null);
  }, [reloadPlaybooks]);

  /**
   * Handle rename modal close
   */
  const handlePlaybookRenameClose = useCallback(() => {
    playbookRenameDisclosure.onClose();
    setPendingRenamePlaybookId(null);
  }, [playbookRenameDisclosure]);

  // ===========================================================================
  // Strategy Actions Handlers (Title Row Popover)
  // ===========================================================================

  /**
   * Handle "Rename" click from strategy actions popover
   */
  const handleStrategyRenameClick = useCallback(() => {
    setIsStrategyActionsPopoverOpen(false);
    renameModalDisclosure.onOpen();
  }, [renameModalDisclosure]);

  /**
   * Handle "Archive" click from strategy actions popover
   */
  const handleStrategyArchiveClick = useCallback(async () => {
    setIsStrategyActionsPopoverOpen(false);
    await archiveStrategy();
  }, [archiveStrategy]);

  /**
   * Handle "Restore" click from strategy actions popover
   */
  const handleStrategyRestoreClick = useCallback(async () => {
    setIsStrategyActionsPopoverOpen(false);
    await restoreStrategy();
  }, [restoreStrategy]);

  // Don't render until we have data
  if (!selectedStrategy) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-text-secondary">Loading...</p>
      </div>
    );
  }

  return (
    <Card radius="none" shadow="none" className="h-full flex flex-col">
      {/* Header - Breadcrumb Navigation + Page Title */}
      <CardHeader className="flex-col items-start pt-1 pb-1 border-b border-default-200">
        <div className="flex items-center">
          <Breadcrumbs variant="light" size="sm">
            <BreadcrumbItem
              onPress={handleNavigateToSettings}
              className="text-text-secondary hover:text-text-primary"
              isCurrent={false}
            >
              Settings
            </BreadcrumbItem>
            <BreadcrumbItem
              onPress={handleBack}
              className="text-text-secondary hover:text-text-primary"
              isCurrent={false}
            >
              Strategies
            </BreadcrumbItem>
          </Breadcrumbs>
        </div>
        <div className="flex items-center justify-between w-full mt-1">
          <h1 className="text-lg font-semibold text-text-primary min-w-0">{selectedStrategy.name}</h1>
          <div className="flex items-center gap-2 shrink-0">
            {selectedStrategy.status === 'archived' && (
              <span className="text-xs text-text-tertiary">Archived</span>
            )}
            {/* Strategy Actions Popover */}
            <Popover
              isOpen={isStrategyActionsPopoverOpen}
              onOpenChange={setIsStrategyActionsPopoverOpen}
              placement="bottom-end"
            >
              <PopoverTrigger>
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  aria-label="Strategy actions"
                  className="opacity-60 hover:opacity-100"
                >
                  <MoreVertical size={16} />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-1">
                <div className="flex flex-col min-w-40">
                  <Button
                    size="md"
                    variant="light"
                    className="justify-start"
                    isDisabled={isSaving}
                    onPress={handleStrategyRenameClick}
                  >
                    Rename
                  </Button>
                  {selectedStrategy.status !== 'archived' && (
                    <Button
                      size="md"
                      variant="light"
                      className="justify-start text-warning-600"
                      isDisabled={isSaving}
                      onPress={handleStrategyArchiveClick}
                    >
                      Archive
                    </Button>
                  )}
                  {selectedStrategy.status === 'archived' && (
                    <Button
                      size="md"
                      variant="light"
                      className="justify-start text-success-600"
                      isDisabled={isSaving}
                      onPress={handleStrategyRestoreClick}
                    >
                      Restore
                    </Button>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </CardHeader>

      {/* Content */}
      <CardBody className="flex-1 px-1 pb-2">
        {/* Conflict Banner */}
        {showConflictBanner && (
          <div className="mx-4 mt-4 rounded-lg bg-warning-50 border border-warning-200 p-3">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-warning-600 shrink-0" />
              <span className="text-sm text-warning-700">Updated in another tab</span>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="flat" onPress={keepEditing}>
                Keep editing
              </Button>
              <Button size="sm" color="warning" onPress={acceptExternalChanges}>
                Accept changes
              </Button>
            </div>
          </div>
        )}

        {/* Strategy Form */}
        <Card radius="none" shadow="none" className="flex flex-col flex-none px-0 pb-4">
          <CardBody className="p-0">
            <StrategyDetailForm
              description={localDescription}
              philosophy={localPhilosophy}
              onDescriptionChange={setLocalDescription}
              onPhilosophyChange={setLocalPhilosophy}
            />
          </CardBody>
        </Card>

        {/* Playbooks Section */}
        <Card radius="sm" shadow="sm" className="w-full min-w-0 overflow-hidden">
          <CardHeader className="border-b border-default-200">
            <div className="flex flex-col gap-2 w-full">
              <h3 className="font-semibold text-text-primary">Playbooks</h3>
              <div className="flex items-center justify-between w-full">
                {playbooks.length > 0 && (
                  <Button
                    size="sm"
                    variant="light"
                    onPress={handleShowArchivedPlaybooksToggle}
                    title={showArchivedPlaybooks ? 'Hide Archived' : 'Show Archived'}
                    aria-label={showArchivedPlaybooks ? 'Hide Archived' : 'Show Archived'}
                    startContent={showArchivedPlaybooks ? <Eye size={16} /> : <EyeOff size={16} />}
                  >
                    Archived
                  </Button>
                )}
                <Popover
                  isOpen={isAddPopoverOpen}
                  onOpenChange={setIsAddPopoverOpen}
                  placement="bottom-end"
                >
                  <PopoverTrigger>
                    <Button
                      size="sm"
                      variant="light"
                      startContent={<Plus size={16} />}
                      className="bg-default-200"
                    >
                      Add
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-1">
                    <div className="flex flex-col min-w-40">
                      <Button
                        size="md"
                        variant="light"
                        className="justify-start"
                        onPress={handleCreatePlaybook}
                      >
                        Create New
                      </Button>
                      <Button
                        size="md"
                        variant="light"
                        className="justify-start"
                        onPress={handleLinkPlaybook}
                      >
                        Link Existing
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </CardHeader>

          <CardBody className="px-4 pb-4 pt-4">
            
            {/* Playbooks Loading */}
            {playbooksLoading && (
              <p className="text-sm text-text-secondary text-center py-4">Loading...</p>
            )}

            {/* Playbooks Error */}
            {playbooksLoadError && !playbooksLoading && (
              <div className="space-y-2">
                <Alert color="danger" variant="flat">
                  Failed to load playbooks.
                </Alert>
                <Button size="sm" variant="flat" onPress={reloadPlaybooks}>
                  Retry
                </Button>
              </div>
            )}

            {/* No playbooks */}
            {!playbooksLoading && !playbooksLoadError && playbooks.length === 0 && (
              <EmptyState
                title="No playbooks"
                description="This strategy has no playbooks yet"
                action={
                  <Button
                    size="sm"
                    variant="flat"
                    color="primary"
                    startContent={<Plus size={14} />}
                    onPress={handleCreatePlaybook}
                  >
                    Add Playbook
                  </Button>
                }
              />
            )}

            {/* All archived */}
            {!playbooksLoading &&
              !playbooksLoadError &&
              playbooks.length > 0 &&
              filteredPlaybooks.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-sm text-text-secondary mb-2">All playbooks archived</p>
                  <Button
                    size="sm"
                    variant="light"
                    startContent={<Eye size={14} />}
                    onPress={handleShowArchivedPlaybooksToggle}
                  >
                    Show Archived
                  </Button>
                </div>
              )}

            {/* Playbook list */}
            {!playbooksLoading && !playbooksLoadError && filteredPlaybooks.length > 0 && (
              <div className="space-y-2">
                {filteredPlaybooks.map((playbook) => (
                  <PlaybookListItem
                    key={playbook.id}
                    playbook={playbook}
                    isSelected={false}
                    isArchived={playbook.status === 'archived'}
                    onSelect={() => handlePlaybookSelect(playbook.id)}
                    sharedStrategyNames={playbookShareMap.get(playbook.id)}
                    onRename={() => handlePlaybookRenameClick(playbook.id)}
                    onDuplicate={() => handleDuplicateClick(playbook.id)}
                    onCopyToStrategy={() => handleCopyToStrategyClick(playbook.id)}
                    onMove={() => handleMoveClick(playbook.id)}
                    onShare={() => handleShareClick(playbook.id)}
                    onUnlink={() => handlePlaybookUnlink(playbook.id)}
                    onArchive={
                      playbook.status !== 'archived'
                        ? () => handlePlaybookArchiveFromList(playbook.id)
                        : undefined
                    }
                    onRestore={
                      playbook.status === 'archived'
                        ? () => handlePlaybookRestoreFromList(playbook.id)
                        : undefined
                    }
                    isActionsDisabled={isPlaybookSaving}
                    showActionsPopover={playbookActionsPopoverId === playbook.id}
                    onActionsPopoverChange={(open) =>
                      setPlaybookActionsPopoverId(open ? playbook.id : null)
                    }
                  />
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </CardBody>

      {/* Footer */}
      <CardFooter className="flex items-center justify-end pt-2 border-t border-default-200">
        <div className="flex items-center gap-1">
          {isDirty && <span className="text-xs text-warning-600">Unsaved</span>}
          <Button
            variant="flat"
            size="sm"
            onPress={discardStrategyChanges}
            isDisabled={!isDirty || isSaving}
          >
            Discard
          </Button>
          <Button
            color="primary"
            size="sm"
            onPress={saveStrategy}
            isDisabled={!isDirty || isSaving}
            isLoading={isSaving}
          >
            Save
          </Button>
        </div>
      </CardFooter>

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        isOpen={unsavedChangesDialog.isOpen}
        onClose={() => {
          unsavedChangesDialog.onClose();
          setPendingNavigation(null);
        }}
        onDiscard={handleDiscard}
      />

      {/* Rename Modal */}
      {selectedStrategy && (
        <StrategyRenameModal
          isOpen={renameModalDisclosure.isOpen}
          onClose={renameModalDisclosure.onClose}
          strategy={selectedStrategy}
          onSave={handleRenameSuccess}
        />
      )}

      {/* Move Playbook Modals */}
      <MoveWithUnsavedChangesDialog
        isOpen={moveUnsavedDisclosure.isOpen}
        onClose={handleMoveCancel}
        onSaveAndMove={handleSaveAndMove}
        onDiscardAndMove={handleDiscardAndMove}
        isSaving={isPlaybookSaving}
      />

      {selectedStrategy && pendingMovePlaybookId && (
        <StrategyPickerModal
          isOpen={movePickerDisclosure.isOpen}
          onClose={handleMoveCancel}
          onConfirm={handleMovePickerConfirm}
          mode="move"
          playbookId={pendingMovePlaybookId}
          currentStrategyId={selectedStrategy.id}
          playbookName={
            playbooks.find((p) => p.id === pendingMovePlaybookId)?.name || 'Playbook'
          }
        />
      )}

      {selectedStrategy && (
        <LastPlaybookWarningDialog
          isOpen={lastPlaybookWarningDisclosure.isOpen}
          onClose={handleMoveCancel}
          onConfirm={handleLastPlaybookWarningConfirm}
          strategyName={selectedStrategy.name}
        />
      )}

      {/* Copy Playbook Modal */}
      {selectedStrategy && pendingCopyPlaybookId && (
        <StrategyPickerModal
          isOpen={copyPickerDisclosure.isOpen}
          onClose={handleCopyCancel}
          onConfirm={handleCopyPickerConfirm}
          mode="copy"
          playbookId={pendingCopyPlaybookId}
          currentStrategyId={selectedStrategy.id}
          playbookName={
            playbooks.find((p) => p.id === pendingCopyPlaybookId)?.name || 'Playbook'
          }
        />
      )}

      {/* Share Playbook Modal */}
      {selectedStrategy && pendingSharePlaybookId && (
        <StrategyPickerModal
          isOpen={sharePickerDisclosure.isOpen}
          onClose={handleShareCancel}
          onConfirm={handleSharePickerConfirm}
          mode="share"
          playbookId={pendingSharePlaybookId}
          currentStrategyId={selectedStrategy.id}
          playbookName={
            playbooks.find((p) => p.id === pendingSharePlaybookId)?.name || 'Playbook'
          }
        />
      )}

      {/* Playbook Rename Modal (Mobile List Only) */}
      {pendingRenamePlaybookId &&
        (() => {
          const playbookToRename = playbooks.find((p) => p.id === pendingRenamePlaybookId);
          return playbookToRename ? (
            <PlaybookRenameModal
              isOpen={playbookRenameDisclosure.isOpen}
              onClose={handlePlaybookRenameClose}
              playbook={playbookToRename}
              onSave={handlePlaybookRenameSuccess}
            />
          ) : null;
        })()}
    </Card>
  );
};
